import { Modal, Typography } from "antd";
import { bulkAction, titleCase, filterArchivedCampaigns } from "@ts/utils";
import { useDispatch, useSelector } from "react-redux";
import { updateBidOrBudgetModalDisplay } from "@features/modalSlice";
import { AiOutlineClose } from "react-icons/ai";
import useGetModulesData from "@/hooks/useGetModulesData";
import { showToast } from "@ts/modals";
import { ActionType, ICampaignsSelectionData, IChannel } from "@ts/common-types";
import { useMemo } from "react";
const { Text } = Typography;

type BidOrBudgetType = 'bid' | 'budget';

interface UpdateBidOrBudgetModalProps {
    type: BidOrBudgetType;
}

interface UpdateBidOrBudgetModalState {
    updateBidOrBudgetModalDisplay: {
        value: boolean;
        type: BidOrBudgetType;
    };
}

const UpdateBidOrBudgetModal = ({ type }: UpdateBidOrBudgetModalProps) => {
    const dispatch = useDispatch();
    const modalState = useSelector((state: UpdateBidOrBudgetModalState) => state.updateBidOrBudgetModalDisplay);
    const handleClose = () => {
        dispatch(updateBidOrBudgetModalDisplay({ value: false, type }));
    };
    const gridParams = useGetModulesData({ type: "grid-params" });
    const campaignsSelectionData = useSelector((state: ICampaignsSelectionData) => state.campaignsSelectionData.value);

    const title = `Update ${titleCase(type)}`;
    const optionText = `Change ${titleCase(type)}`;
    const inputId = `update${titleCase(type)}Input`;
    const buttonId = `update${titleCase(type)}Btn`;
    const excludedProgramType = type === 'bid' ? gridParams.excludedBidProgramType : gridParams.excludedBudgetProgramType;


    const modalNote = useMemo(() => {
        if (gridParams?.isArchivedCampaignsSelectionAllowed) {

            const { message, isAllSelectedArchived } = filterArchivedCampaigns(campaignsSelectionData, false);
            if (isAllSelectedArchived) {
                showToast("danger", message);
                dispatch(updateBidOrBudgetModalDisplay({ value: false, type: 'budget' }))
            } else {
                return message;
            }
        }
    }, [modalState, campaignsSelectionData, gridParams]);

    const handleBulkAction = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            await bulkAction(
                e,
                modalState.value,
                (arg: boolean) => dispatch(updateBidOrBudgetModalDisplay({ value: arg, type })),
                excludedProgramType || undefined
            );
        } catch (error: any) {
            showToast(
                "danger",
                error?.response?.data?.message || "An error occurred while updating. Please try again later."
            );
            handleClose();
        }
    };
    
    return (
        <>
            <Modal
                title={
                    <div>
                        <div className={`d-flex align-items-center ${modalNote ? "" : "pb-3"} fs-4 fw-semibold`}>
                            {title}

                        </div>
                        {modalNote ?
                            <div >
                                <Text type="danger" className="fs-7">Note - {modalNote}</Text>
                            </div>
                            : ""}
                    </div>
                } 
                open={modalState.value && modalState.type === type} 
                onCancel={handleClose} 
                closeIcon={<><AiOutlineClose className="fs-4" /></>} 
                centered 
                footer={null}
            >
                <div className="p-4">
                    <div className="input-group input-group-sm mb-4">
                        <select
                            className="form-select bg-light border-0 fw-semibold"
                            id={`update${titleCase(type)}Option`}
                            style={{ maxWidth: 200, height: "4rem" }}
                        >
                            <option value={ActionType.ABSOLUTE}>{optionText}</option>
                            <option value={ActionType.PERC_INC}>
                                Increase {titleCase(type)} by %
                            </option>
                            <option value={ActionType.PERC_DEC}>
                                Decrease {titleCase(type)} by %
                            </option>
                        </select>
                        <input
                            className="form-control"
                            id={inputId}
                            type="text"
                        />
                    </div>
                    <button
                        className="btn btn-primary px-4 py-2"
                        data-bs-name="Update"
                        id={buttonId}
                        type="button"
                        onClick={handleBulkAction}
                    >
                        Update
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default UpdateBidOrBudgetModal; 